import type { ApolloClient } from '@apollo/client';

import {
  getUserToken,
  removeUserToken,
  setUserToken
} from '@/shared/services/user-hash';
import { dispatch } from '@/store';
import { setIsAuthorized } from '@/store/auth.slice';
import { resetConnection } from '@/store/connection.slice';
import { resetSession } from '@/store/session.slice';

import { wsClient } from './apollo/links/ws.link';
import { LOGOUT_MUTATION, REFRESH_MUTATION } from './auth.api';
import { postSessionMessage } from './session-channel';

const BEARER_PREFIX = 'Bearer ';

/** Значение заголовка `Authorization` для accessToken. */
export const toBearer = (accessToken: string): string =>
  `${BEARER_PREFIX}${accessToken}`;

/** accessToken из заголовка `Authorization`; без заголовка или не bearer — пустая строка. */
export const fromBearer = (authorization: unknown): string =>
  typeof authorization === 'string' && authorization.startsWith(BEARER_PREFIX)
    ? authorization.slice(BEARER_PREFIX.length)
    : '';

/**
 * Сбрасывает сессию сотрудника: токен, флаг авторизации, профиль, сокет подписок
 * и кэш Apollo (данные одного сотрудника не должны достаться другому).
 * Вызывается при выходе и при истечении сессии (не удался refresh, сокет
 * закрыт по токену). На вход с `savedUrl` уводит ProtectedRoute, увидев
 * `isAuthorized: false`, — сама функция не навигирует.
 *
 * Остальные вкладки получают `logout` и сбрасывают свои данные тоже. Вкладка,
 * которая сбрасывает сессию по такому сообщению, передаёт `broadcast: false`:
 * иначе вкладки пересылали бы его друг другу бесконечно.
 */
export const clearSession = async (
  client: ApolloClient,
  { broadcast = true }: { broadcast?: boolean } = {}
): Promise<void> => {
  removeUserToken();
  dispatch(setIsAuthorized(false));
  dispatch(resetSession());
  wsClient.terminate();
  dispatch(resetConnection());
  if (broadcast) postSessionMessage({ type: 'logout' });
  await client.clearStore();
};

/**
 * Выход: просит бэкенд отозвать refresh-куку и сбрасывает сессию во всех
 * вкладках. Сессия сбрасывается, даже если `logout` не дошёл (нет связи).
 */
export const endSession = async (client: ApolloClient): Promise<void> => {
  try {
    await client.mutate({ mutation: LOGOUT_MUTATION });
  } catch {
    // Сотрудник выходит в любом случае: без связи сессия сбрасывается локально.
  }
  await clearSession(client);
};

let pendingRefresh: Promise<string> | null = null;

/** Обновляет accessToken по refresh-куке. Параллельные вызовы ждут один и тот же запрос. */
const refreshAccessToken = (client: ApolloClient): Promise<string> => {
  pendingRefresh ??= client
    .mutate({ mutation: REFRESH_MUTATION, fetchPolicy: 'no-cache' })
    .then(({ data }) => {
      const accessToken = data?.refresh.accessToken;
      if (!accessToken) throw new Error('Refresh returned no access token');

      setUserToken({ accessToken });
      return accessToken;
    })
    .finally(() => {
      pendingRefresh = null;
    });

  return pendingRefresh;
};

/**
 * accessToken для повтора запроса, который сервер отклонил с `rejectedToken`.
 * Если токен в куке уже другой — его обновил параллельный запрос, и новый
 * refresh не нужен. Иначе — refresh. Не удался refresh — промис отклоняется,
 * сброс сессии остаётся за вызывающим.
 */
export const getFreshAccessToken = (
  client: ApolloClient,
  rejectedToken: string
): Promise<string> => {
  const { accessToken } = getUserToken();
  if (accessToken && accessToken !== rejectedToken) {
    return Promise.resolve(accessToken);
  }
  return refreshAccessToken(client);
};
